/**
 * MCP Setup Card
 *
 * Simple pending-connection card for MCP servers, styled like the
 * inline location/connector prompts: one signpost row for the server,
 * then a single ask at the bottom derived from its auth type
 * (OAuth button, credential field, or a find-server search).
 *
 * Primary responsibilities:
 * - Parse setup payloads from the unified integrations API
 * - Ask only for the auth the server actually needs
 * - Live-refresh to Connected after OAuth completes
 *
 * Dependencies:
 * - Unified integrations API
 * - Sonner toast notifications
 */

'use client'

import { Check, KeyRound, Loader2, ServerCog } from 'lucide-react'
import { toast } from 'sonner'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { ChatbotUIContext } from '@/context/context'
import { cn } from '@/lib/utils'

export type McpAuthMode = 'oauth' | 'api_key' | 'manual'

export type McpSetupAuthOption = {
  actionLabel: string
  description: string
  label: string
  mode: McpAuthMode
}

export type McpSetupAuthCard = {
  actionLabel: string
  actionUrl: string | null
  authType: string | null
  cardState: string
  description: string
  integrationId: string
  name: string
  setupUrl: string | null
  warnings: string[]
}

export type McpSetupCard = {
  authOptions: McpSetupAuthOption[]
  authCard?: McpSetupAuthCard
  description: string
  registryUrl: string
  requestedName: string
  serverQuery: string | null
  title: string
}

interface McpSetupCardViewProps {
  className?: string
  compact?: boolean
  setup: McpSetupCard
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function getAuthMode(value: unknown): McpAuthMode | null {
  if (value === 'oauth' || value === 'api_key' || value === 'manual') {
    return value
  }

  return null
}

function getMcpAuthOption(value: unknown): McpSetupAuthOption | null {
  if (!isRecord(value)) {
    return null
  }

  const mode = getAuthMode(value.mode)
  const label = getString(value.label)
  const description = getString(value.description)
  const actionLabel = getString(value.action_label)

  if (!mode || !label || !description || !actionLabel) {
    return null
  }

  return { actionLabel, description, label, mode }
}

function getStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.flatMap((item) => (typeof item === 'string' && item.trim() ? [item.trim()] : []))
    : []
}

function getMcpSetupAuthCard(value: unknown): McpSetupAuthCard | undefined {
  if (!isRecord(value)) {
    return undefined
  }

  const integrationId = getString(value.integrationId) ?? getString(value.integration_id)
  const name = getString(value.name)
  const description = getString(value.description)
  const action = isRecord(value.action) ? value.action : {}

  if (!integrationId || !name || !description) {
    return undefined
  }

  return {
    actionLabel: getString(action.label) ?? 'Continue setup',
    actionUrl:
      getString(action.authorizationUrl) ??
      getString(action.authorization_url) ??
      getString(value.authorizationUrl) ??
      getString(value.authorization_url),
    authType:
      getString(value.authType) ?? getString(value.auth_type) ?? getString(action.kind),
    cardState: getString(value.cardState) ?? getString(value.card_state) ?? 'pending',
    description,
    integrationId,
    name,
    setupUrl: getString(value.setupUrl) ?? getString(value.setup_url),
    warnings: getStringArray(value.warnings)
  }
}

export function getMcpSetupCard(value: unknown): McpSetupCard | null {
  if (!isRecord(value)) {
    return null
  }

  const setup = isRecord(value.setupCard)
    ? value.setupCard
    : isRecord(value.setup_card)
      ? value.setup_card
      : value
  const authCard =
    getMcpSetupAuthCard(value.authCard) ??
    getMcpSetupAuthCard(value.auth_card) ??
    getMcpSetupAuthCard(setup.auth_card)
  const authOptions = Array.isArray(setup.auth_options)
    ? setup.auth_options
        .map(getMcpAuthOption)
        .filter((option): option is McpSetupAuthOption => Boolean(option))
    : []
  const description =
    getString(setup.description) ??
    authCard?.description ??
    'Losi prepared this MCP server for authentication.'
  const registryUrl =
    getString(setup.registry_url) ??
    getString(value.registry_url) ??
    authCard?.setupUrl ??
    'https://registry.modelcontextprotocol.io/'
  const requestedName = getString(setup.requested_name) ?? authCard?.name ?? 'MCP server'
  const title = getString(setup.title) ?? (authCard ? `Connect ${authCard.name}` : null)

  if (!description || !registryUrl || !requestedName || !title) {
    return null
  }

  return {
    authOptions,
    authCard,
    description,
    registryUrl,
    requestedName,
    serverQuery: getString(setup.server_query),
    title
  }
}

function normalizeCardState(value: string | null | undefined) {
  return (value ?? '').trim().toLowerCase().replace(/[-\s]+/g, '_')
}

function isConnectedState(value: string | null | undefined) {
  const normalized = normalizeCardState(value)
  return normalized === 'connected' || normalized === 'ready' || normalized === 'active'
}

function readMcpIntegrationIdFromPayload(data: Record<string, unknown>) {
  return (
    getString(data.mcpIntegrationId) ??
    getString(data.mcp_integration_id) ??
    (getString(data.integrationId) !== 'mcp' ? getString(data.integrationId) : null) ??
    (getString(data.integration_id) !== 'mcp' ? getString(data.integration_id) : null)
  )
}

function openSetupUrl(url: string | null | undefined, label: string) {
  if (!url) {
    toast('Continue from Integrations to finish this MCP setup.')
    return
  }

  const popup = window.open(url, 'losi-mcp-auth', 'height=760,width=720')

  if (!popup) {
    toast.error(`Allow popups to ${label.toLowerCase()}`)
  }
}

// --------------

type McpAskMode = 'oauth' | 'api_key' | 'basic' | 'none' | 'manual' | 'find-server'

// One ask per server: derive it from the integration's real auth type
// (falling back to card state), never a menu of auth options.
function resolveMcpAskMode(setup: McpSetupCard, cardState: string): McpAskMode {
  const normalizedState = normalizeCardState(cardState)

  if (
    normalizedState === 'unsupported_local' ||
    normalizedState === 'security_review' ||
    normalizedState === 'needs_configuration'
  ) {
    return 'manual'
  }

  const authType = setup.authCard?.authType?.trim().toLowerCase() ?? ''

  if (authType === 'oauth') return 'oauth'
  if (authType === 'api_key' || authType === 'bearer_token' || authType === 'custom') {
    return 'api_key'
  }
  if (authType === 'basic') return 'basic'
  if (authType === 'none') return 'none'

  if (setup.authCard) {
    return normalizedState === 'needs_oauth' ? 'oauth' : 'manual'
  }

  const optionModes = new Set(setup.authOptions.map((option) => option.mode))

  if (optionModes.size === 1) {
    const [onlyMode] = [...optionModes]
    if (onlyMode === 'oauth') return 'oauth'
    if (onlyMode === 'api_key') return 'api_key'
    return 'manual'
  }

  return 'find-server'
}

function getMcpAuthCaption(mode: McpAskMode, setup: McpSetupCard) {
  switch (mode) {
    case 'oauth':
      return 'Signs in with OAuth in your browser'
    case 'api_key':
      return setup.authCard?.authType?.toLowerCase() === 'bearer_token'
        ? 'Needs a bearer token'
        : 'Needs an API key'
    case 'basic':
      return 'Needs a username and password'
    case 'none':
      return 'No sign-in needed'
    case 'manual':
      return 'Finished in Integrations'
    case 'find-server':
      return 'Find the server, then sign in'
  }
}

function getMcpCredentialLabel(setup: McpSetupCard) {
  const authType = setup.authCard?.authType?.toLowerCase()

  if (authType === 'bearer_token') return 'Bearer token'
  if (authType === 'custom') return 'Credential'
  return 'API key'
}

const primaryButtonClassName =
  'bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
const askInputClassName =
  'border-border bg-background w-full rounded-md border px-3 py-2 text-sm'

// --------------

export function McpSetupCardView({
  className,
  compact = false,
  setup: initialSetup
}: McpSetupCardViewProps) {
  const { selectedWorkspace } = useContext(ChatbotUIContext)
  const workspaceId = selectedWorkspace?.id ?? null

  // Find-server can replace the generic payload with the real setup card.
  const setupSyncKey = `${initialSetup.authCard?.integrationId ?? ''}:${initialSetup.title}:${initialSetup.requestedName}`
  const [liveSetup, setLiveSetup] = useState(initialSetup)
  const liveSetupKeyRef = useRef(setupSyncKey)

  useEffect(() => {
    if (liveSetupKeyRef.current === setupSyncKey) {
      return
    }

    liveSetupKeyRef.current = setupSyncKey
    setLiveSetup(initialSetup)
  }, [initialSetup, setupSyncKey])

  const setup = liveSetup
  const integrationId = setup.authCard?.integrationId ?? null
  const initialCardState = setup.authCard?.cardState ?? 'pending_auth'
  const [liveCardState, setLiveCardState] = useState(initialCardState)

  useEffect(() => {
    setLiveCardState(initialCardState)
  }, [initialCardState, integrationId])

  const markConnected = useCallback((nextState = 'connected') => {
    setLiveCardState(nextState)
  }, [])

  const refreshStatus = useCallback(async () => {
    if (!integrationId) {
      return
    }

    try {
      const response = await fetch(`/api/integrations/mcp/${encodeURIComponent(integrationId)}`, {
        method: 'GET',
        credentials: 'include'
      })

      if (!response.ok) {
        return
      }

      const data = (await response.json()) as unknown
      if (!isRecord(data)) {
        return
      }

      const nextState =
        getString(data.cardState) ?? getString(data.card_state) ?? getString(data.status)

      if (nextState) {
        setLiveCardState(nextState)
      }
    } catch {
      // Keep the last known card state when status refresh fails.
    }
  }, [integrationId])

  useEffect(() => {
    if (!integrationId || isConnectedState(initialCardState)) {
      return
    }

    void refreshStatus()
  }, [integrationId, initialCardState, refreshStatus])

  useEffect(() => {
    if (!integrationId) {
      return
    }

    const applyConnectedFromEvent = (data: Record<string, unknown>, announce: boolean) => {
      const nextState = getString(data.status)
      const connectedNext =
        nextState && isConnectedState(nextState) ? nextState : data.success === true ? 'connected' : null

      if (!connectedNext) {
        void refreshStatus()
        return
      }

      markConnected(connectedNext)

      if (announce) {
        toast.success(
          typeof data.message === 'string' && data.message.trim()
            ? data.message
            : `${setup.authCard?.name ?? setup.requestedName} connected`
        )
      }
    }

    const handleConnectionPayload = (data: unknown, announce: boolean) => {
      if (!isRecord(data)) {
        return
      }

      if (data.type !== 'EXTENSION_CONNECTED' && data.type !== 'INTEGRATION_CONNECTED') {
        return
      }

      const payloadIntegrationId = readMcpIntegrationIdFromPayload(data)
      if (payloadIntegrationId && payloadIntegrationId !== integrationId) {
        return
      }

      // Generic mcp provider events without an id still refresh this card.
      if (!payloadIntegrationId && getString(data.integrationId) !== 'mcp') {
        return
      }

      if (data.success === false) {
        void refreshStatus()
        return
      }

      applyConnectedFromEvent(data, announce)
    }

    const handleMessage = (event: MessageEvent) => {
      handleConnectionPayload(event.data, true)
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== 'losi:integration:oauth-result' || !event.newValue) {
        return
      }

      try {
        handleConnectionPayload(JSON.parse(event.newValue), true)
      } catch {
        void refreshStatus()
      }
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refreshStatus()
      }
    }

    const channel =
      typeof BroadcastChannel === 'undefined'
        ? null
        : new BroadcastChannel('losi:integration:oauth')

    try {
      const cached = localStorage.getItem('losi:integration:oauth-result')
      if (cached) {
        // Hydrate silently from the last oauth result; API refresh covers truth.
        handleConnectionPayload(JSON.parse(cached), false)
      }
    } catch {
      // Ignore malformed cached oauth results.
    }

    window.addEventListener('message', handleMessage)
    window.addEventListener('storage', handleStorage)
    window.addEventListener('focus', handleVisibility)
    document.addEventListener('visibilitychange', handleVisibility)
    channel?.addEventListener('message', (event: MessageEvent) => {
      handleConnectionPayload(event.data, true)
    })

    return () => {
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('focus', handleVisibility)
      document.removeEventListener('visibilitychange', handleVisibility)
      channel?.close()
    }
  }, [integrationId, markConnected, refreshStatus, setup.authCard?.name, setup.requestedName])

  const connected = isConnectedState(liveCardState)
  const serverName = setup.authCard?.name ?? setup.requestedName
  const askMode = resolveMcpAskMode(setup, liveCardState)

  const [credential, setCredential] = useState('')
  const [username, setUsername] = useState('')
  const [serverQueryInput, setServerQueryInput] = useState(setup.serverQuery ?? '')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isWaitingForAuth, setIsWaitingForAuth] = useState(false)
  const [askError, setAskError] = useState('')

  useEffect(() => {
    if (connected) {
      setIsWaitingForAuth(false)
    }
  }, [connected])

  // Single round-trip for every bottom ask. The API is the source of
  // truth: popup (OAuth), refresh (credential saved), or setup (more
  // details needed, which replaces this card's payload).
  const runAuthAction = useCallback(
    async (fields: Record<string, unknown>) => {
      if (!workspaceId) {
        setAskError('Open this from a workspace to finish connecting.')
        return
      }

      setIsSubmitting(true)
      setAskError('')

      try {
        const response = await fetch('/api/integrations', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: integrationId ? 'reconnect' : 'connect',
            integration_id: 'mcp',
            opener_origin: window.location.origin,
            workspace_id: workspaceId,
            ...(integrationId
              ? { mcp_auth_only: true, mcp_integration_id: integrationId }
              : {}),
            ...fields
          })
        })
        const data: unknown = await response.json().catch(() => null)

        if (!isRecord(data)) {
          throw new Error('MCP connection failed')
        }

        if (!response.ok || data.success === false) {
          throw new Error(
            getString(data.error) ?? getString(data.message) ?? 'MCP connection failed'
          )
        }

        const mode = getString(data.mode)
        const nextSetup = getMcpSetupCard(data)

        if (mode === 'popup') {
          const url = getString(data.url)
          if (url) {
            openSetupUrl(url, 'MCP authorization')
            setIsWaitingForAuth(true)
            return
          }
        }

        if (nextSetup && (mode === 'setup' || !integrationId)) {
          liveSetupKeyRef.current = `${nextSetup.authCard?.integrationId ?? ''}:${nextSetup.title}:${nextSetup.requestedName}`
          setLiveSetup(nextSetup)
          if (isConnectedState(nextSetup.authCard?.cardState)) {
            markConnected(nextSetup.authCard?.cardState ?? 'connected')
          }
          return
        }

        markConnected()
        setCredential('')
        toast.success(getString(data.message) ?? `${serverName} connected`)
      } catch (error) {
        setAskError(error instanceof Error ? error.message : 'MCP connection failed')
      } finally {
        setIsSubmitting(false)
      }
    },
    [integrationId, markConnected, serverName, workspaceId]
  )

  const handleOAuthConnect = useCallback(() => {
    const actionUrl = setup.authCard?.actionUrl

    if (actionUrl) {
      openSetupUrl(actionUrl, setup.authCard?.actionLabel ?? 'MCP authorization')
      setIsWaitingForAuth(true)
      return
    }

    void runAuthAction({})
  }, [runAuthAction, setup.authCard?.actionLabel, setup.authCard?.actionUrl])

  const handleCredentialSubmit = useCallback(() => {
    const secret = credential.trim()

    if (askMode === 'basic') {
      if (!username.trim() || !secret) {
        setAskError('Enter a username and password first.')
        return
      }

      void runAuthAction({ mcp_password: secret, mcp_username: username.trim() })
      return
    }

    if (!secret) {
      setAskError(`Enter your ${getMcpCredentialLabel(setup).toLowerCase()} first.`)
      return
    }

    void runAuthAction({
      mcp_credential: secret,
      ...(setup.authCard?.authType?.toLowerCase() === 'api_key'
        ? { mcp_header_name: 'X-API-Key' }
        : {})
    })
  }, [askMode, credential, runAuthAction, setup, username])

  const handleFindServer = useCallback(() => {
    const query = serverQueryInput.trim()

    if (!query) {
      setAskError('Enter a server name or URL first.')
      return
    }

    void runAuthAction({ mcp_query: query })
  }, [runAuthAction, serverQueryInput])

  if (connected) {
    return (
      <div
        className={cn(
          'border-border/40 bg-card/50 rounded-xl border p-4 backdrop-blur-sm',
          compact ? 'max-w-sm' : 'max-w-md',
          className
        )}
      >
        <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
          <Check className="size-4" />
          <span className="text-sm font-medium">{serverName} connected</span>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'border-border/40 bg-card/50 rounded-xl border p-4 backdrop-blur-sm',
        compact ? 'max-w-sm' : 'max-w-md',
        className
      )}
    >
      <div className="flex items-center gap-2">
        <ServerCog className="text-primary size-4" />
        <span className="text-sm font-medium">{setup.title}</span>
      </div>

      <p className="text-muted-foreground mt-2 text-sm">{setup.description}</p>

      <p className="text-muted-foreground/70 mt-2 text-[11px]">
        Losi can use this MCP server in chat once it&apos;s connected.
      </p>

      <div className="border-border/60 mt-3 flex w-full items-start gap-3 rounded-lg border p-3">
        <span className="bg-muted text-foreground flex size-8 shrink-0 items-center justify-center rounded-lg">
          <ServerCog className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{serverName}</span>
          <span className="text-muted-foreground mt-0.5 block text-xs">
            {getMcpAuthCaption(askMode, setup)}
          </span>
        </span>
      </div>

      {setup.authCard?.warnings[0] ? (
        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
          {setup.authCard.warnings[0]}
        </p>
      ) : null}

      {askMode === 'oauth' ? (
        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              className={primaryButtonClassName}
              disabled={isSubmitting}
              onClick={handleOAuthConnect}
              type="button"
            >
              {setup.authCard?.actionLabel ?? `Connect ${serverName}`}
            </button>
            {setup.authCard?.setupUrl ? (
              <button
                className="text-muted-foreground text-xs underline"
                onClick={() => openSetupUrl(setup.authCard?.setupUrl, 'Open Integrations')}
                type="button"
              >
                Open Integrations
              </button>
            ) : null}
          </div>
          {isWaitingForAuth ? (
            <div className="text-muted-foreground mt-2 flex items-center gap-2 text-xs">
              <Loader2 className="size-3.5 animate-spin" />
              Waiting for authorization...
            </div>
          ) : null}
        </div>
      ) : null}

      {askMode === 'api_key' || askMode === 'basic' ? (
        <div className="border-border/50 mt-3 space-y-2 rounded-lg border border-dashed p-3">
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            <KeyRound className="size-3.5" />
            {askMode === 'basic'
              ? `Enter the username and password for ${serverName}`
              : `Paste your ${getMcpCredentialLabel(setup).toLowerCase()} for ${serverName}`}
          </div>
          {askMode === 'basic' ? (
            <input
              autoComplete="username"
              className={askInputClassName}
              onChange={(event) => setUsername(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleCredentialSubmit()
                }
              }}
              placeholder="Username"
              type="text"
              value={username}
            />
          ) : null}
          <input
            autoComplete="off"
            className={askInputClassName}
            onChange={(event) => setCredential(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleCredentialSubmit()
              }
            }}
            placeholder={askMode === 'basic' ? 'Password' : getMcpCredentialLabel(setup)}
            type="password"
            value={credential}
          />
          <button
            className={primaryButtonClassName}
            disabled={isSubmitting}
            onClick={handleCredentialSubmit}
            type="button"
          >
            {isSubmitting ? 'Connecting...' : `Connect ${serverName}`}
          </button>
        </div>
      ) : null}

      {askMode === 'none' ? (
        <p className="text-muted-foreground mt-3 text-xs">
          No sign-in needed — Losi can use this server now.
        </p>
      ) : null}

      {askMode === 'manual' ? (
        <div className="mt-3">
          <button
            className={primaryButtonClassName}
            onClick={() => openSetupUrl(setup.authCard?.setupUrl, 'Open Integrations')}
            type="button"
          >
            Finish in Integrations
          </button>
        </div>
      ) : null}

      {askMode === 'find-server' ? (
        <div className="mt-3 space-y-2">
          <div className="flex gap-2">
            <input
              className={askInputClassName}
              onChange={(event) => setServerQueryInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleFindServer()
                }
              }}
              placeholder="Server name or URL"
              type="text"
              value={serverQueryInput}
            />
            <button
              className={cn(primaryButtonClassName, 'shrink-0')}
              disabled={isSubmitting}
              onClick={handleFindServer}
              type="button"
            >
              {isSubmitting ? 'Finding...' : 'Find server'}
            </button>
          </div>
          <a
            className="text-primary inline-block text-xs underline"
            href={setup.registryUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            Search the MCP registry
          </a>
        </div>
      ) : null}

      {askError ? <p className="mt-3 text-xs text-red-500">{askError}</p> : null}

      {isSubmitting && askMode !== 'api_key' && askMode !== 'basic' && askMode !== 'find-server' ? (
        <div className="text-muted-foreground mt-3 flex items-center gap-2">
          <Loader2 className="size-3.5 animate-spin" />
          <span className="text-xs">Connecting...</span>
        </div>
      ) : null}
    </div>
  )
}
